<template>
  <div>
    <div
      class="spring-spinner"
      style="margin-top: 48px"
      v-if="!thereIsNoOrders && !errorMessage && !orders.length"
    >
      <div class="spring-spinner-part top">
        <div class="spring-spinner-rotator"></div>
      </div>
      <div class="spring-spinner-part bottom">
        <div class="spring-spinner-rotator"></div>
      </div>
    </div>
    <div
      class="tab-container"
      @scroll="scroll($event)"
      :style="`max-height: ${maxHeight}px !important;`"
      v-else-if="orders.length"
    >
      <OrderCard
        v-for="(order, index) in orders"
        :key="order.id"
        :data="order"
        @cancelled="removeOrder($event)"
        :besoin="besoin"
        :show_status="showStatusPerCard"
      />
      <div class="spring-spinner" v-if="loading">
        <div class="spring-spinner-part top">
          <div class="spring-spinner-rotator"></div>
        </div>
        <div class="spring-spinner-part bottom">
          <div class="spring-spinner-rotator"></div>
        </div>
      </div>
    </div>
    <div v-if="errorMessage">
      <p class="message">{{ errorMessage }}</p>
    </div>
    <div v-else-if="thereIsNoOrders">
      <p class="message">{{ $t("requestsPage.noOrders") }}</p>
    </div>
  </div>
</template>

<script>
import OrderCard from "@/components/dispatching/OrderCard";
import { companyNow } from "@/util/companyTime";
import axios from "axios";

// The board's list endpoint pages 5 rows at a time (BookingController::index
// defaults perPage to 5), which is the unit `page` and `lastPage` are counted
// in everywhere below.
const PAGE_SIZE = 5;
// Ceiling on how deep a single self-heal may re-ask: that list query is an
// 18-table join, so a column the operator scrolled a long way down must not
// turn the 30 s tick into a hundreds-of-rows request. Rows deeper than this
// are simply left alone by the refresh.
const MAX_REFRESH_PAGES = 10;

export default {
  name: "InfinityScroll",
  components: {
    OrderCard,
  },
  props: {status: {
    required: false
  }, multiStatus: {
    required: false
  }, time: {
    required: false
  }, besoin: {
    required: false
  }, passengerId: {
    required: false,
    default: null
  }, maxHeight: {
    required: false,
    default: '553'
  },showStatusPerCard: {
    type: Boolean,
    required:false,
    default: false
  }},
  computed: {
    statuses: function () {
      if (!this.status && this.multiStatus) return this.multiStatus.split("|");
      else return false;
    },
    thereIsNoOrders() {
      if (this.loading) return false;
      else if (this.orders.length > 0) return false;
      else return true;
    },
  },
  data() {
    return {
      orders: [],
      errorMessage: "",
      page: 1,
      lastPage: 0,
      loading: true,
    };
  },
  created() {
    // Non-reactive refresh bookkeeping. `_refreshToken` is a generation counter
    // so an older in-flight self-heal can never apply after a newer one, and
    // `_refreshing` is a single-flight flag so two of them can never reconcile
    // the same rows twice.
    this._refreshToken = 0;
    this._refreshing = false;
    if(!this.passengerId) {
      if (!this.multiStatus) this.initData([this.status]);
      else {
        this.initData(this.statuses);

      }
    }
  },
  watch: {
    time: function () {
      // The column is rebuilt for a different time filter here, so a self-heal
      // still in flight for the old one must land nowhere.
      this._refreshToken++;
      this.orders = [];
      this.loading = true;
      if (!this.multiStatus) this.initData(this.status);
      else {
       this.initData(this.statuses);
      }
    },
    besoin: function () {
      this.orders.forEach((el, index) => {
        if (el.id === this.besoin) {
          el.besoin = this.besoin;
          console.log("BESOIN RENEW");
          console.log(this.besoin);
        } else {
          el.besoin = false;
        }
      });
    },
    passengerId: {
      handler: function (newId) {
        this.getOrdersByPassengerId(newId);
      },
      immediate:true
    }
  },
  methods: {
    removeOrder(id) {
      let order;
      console.log("Order to cancel: ", id);
      for (let i = 0; i < this.orders.length; i++) {
        if (this.orders[i].id && this.orders[i].id == id) {
            order = this.orders[i];
            this.orders.splice(i, 1);
            if (order.now == '0')
              this.$emit('decreaseCount');
            return order || null;
          }
      }
    },
    addOrder(data) {
      this.orders.unshift(data);
    },
    initData(statuses) {
      this.$store
        .dispatch("getOrders", {
          statuses: statuses,
          time: this.time,
          filter: "BOTH",
        })
        .then((response) => {
          if (response.data.length) {
            this.lastPage = response.last_page;
            response.data.forEach((el) => {
              this.orders.push(el);
              this.$emit("loaded", el);
              this.$emit("updateTotal", response.total);
            });
          }
        })
        .catch((error) => {
          this.errorMessage = error;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Board self-heal. The board is otherwise socket-only (initData runs once
    // from created()), so a single missed Timeline:update_booking_status frame
    // freezes a card until a manual F5.
    //
    // This is a RECONCILE, not a reset: it re-asks the server for exactly the
    // depth the operator has already paged in and merges the answer into the
    // array that is on screen. `orders` is never emptied, `loading` is never
    // raised and `page` is never rewound, so the cards, the OrderCard instances
    // (with whatever dialog is open inside them), the scroll offset and every
    // extra page that was scrolled in all survive the refresh.
    refreshInPlace() {
      if (this.passengerId) return;
      // Another self-heal is still running, or a first load / infinite-scroll
      // page is in flight - that response would land on top of ours and append
      // the same rows twice. Skip this tick entirely; the next one is 30 s away.
      if (this._refreshing || this.loading) return;

      const token = ++this._refreshToken;
      this._refreshing = true;
      const statuses = this.multiStatus ? this.statuses : [this.status];
      const depth = Math.min(Math.max(this.page, 1), MAX_REFRESH_PAGES);
      const perPage = depth * PAGE_SIZE;
      // Rows the socket adds while the request is in flight are not part of the
      // answer we are about to apply, so they must never be reconciled away.
      const known = this.orders.map((el) => Number(el.id));

      this.$store
        .dispatch("getOrders", {
          statuses: statuses,
          time: this.time,
          filter: "BOTH",
          perPage: perPage,
        })
        .then((response) => {
          if (token !== this._refreshToken) return;
          this.applyRefresh(response, perPage, known);
        })
        .catch((error) => {
          // A background poll must never blank a working column: keep every
          // card exactly as it is, set no error state, wait for the next tick.
          console.log("Board refresh failed, keeping the current orders:", error);
        })
        .finally(() => {
          // Only one refresh can be in flight per column (guarded above), so
          // this always belongs to the cycle that is finishing.
          this._refreshing = false;
        });
    },
    // Merge a refresh response into `orders` without recreating a single card.
    applyRefresh(response, perPage, known) {
      const rows = Array.isArray(response.data) ? response.data : [];
      const freshById = new Map();
      rows.forEach((el) => freshById.set(Number(el.id), el));
      const knownIds = new Set(known);

      // filterBookings orders by bookings.id DESC, so a FULL window means the
      // server had at least `perPage` matching rows and everything with a
      // smaller id than the last one it returned sits on a page we did not ask
      // for. Those rows are out of scope for this refresh and must survive: an
      // order is only dropped when it falls inside the window we really
      // re-fetched and the server no longer returns it there.
      let cutoffId = null;
      if (rows.length && rows.length >= perPage) {
        cutoffId = rows.reduce(function (min, el) {
          const id = Number(el.id);
          return isNaN(id) || id > min ? min : id;
        }, Infinity);
        if (!isFinite(cutoffId)) cutoffId = null;
      }

      const merged = [];
      this.orders.forEach((order) => {
        const id = Number(order.id);
        const fresh = freshById.get(id);
        if (fresh) {
          // Same object, same component instance, same open dialog.
          this.mergeOrderFields(order, fresh);
          merged.push(order);
          return;
        }
        // Arrived (or unidentifiable) after the request went out: not covered.
        if (isNaN(id) || !knownIds.has(id)) {
          merged.push(order);
          return;
        }
        // Lives past the depth we re-fetched: not covered either.
        if (cutoffId !== null && id < cutoffId) {
          merged.push(order);
          return;
        }
        // Inside the re-fetched window and gone from it: it really left this
        // column (cancelled, or moved on while a socket frame was lost).
      });

      // Splice genuinely new rows in at the position the server gave them,
      // relative to the rows that are already on screen.
      let insertAt = 0;
      rows.forEach((row) => {
        const id = Number(row.id);
        const index = merged.findIndex((el) => Number(el.id) === id);
        if (index !== -1) {
          insertAt = index + 1;
          return;
        }
        merged.splice(insertAt, 0, row);
        insertAt += 1;
      });

      this.orders = merged;
      // A successful refresh is the only thing allowed to clear an error left
      // by a failed first load.
      this.errorMessage = "";
      const total = Number(response.total);
      if (!isNaN(total) && isFinite(total)) {
        // `lastPage` stays in PAGE_SIZE units: loadData() keeps paging with the
        // endpoint's default perPage, so the bigger window asked for here must
        // not leak into the infinite-scroll bookkeeping.
        this.lastPage = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0;
        this.$emit("updateTotal", total);
      }
      // The board rebuilds this column's id bookkeeping from what actually
      // landed, instead of wiping every column up front and hoping.
      this.$emit("reconciled", {
        keys: this.multiStatus ? this.statuses : [this.status],
        rows: merged.map((el) => ({ id: el.id, status: el.status })),
      });
    },
    // Copy the server's fields onto the row the column already renders. Keys
    // the response does not carry (the local `besoin` flag) are left alone, and
    // keys the row does not have yet go through $set so Vue 2 tracks them.
    mergeOrderFields(order, fresh) {
      Object.keys(fresh).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(order, key)) {
          if (order[key] !== fresh[key]) order[key] = fresh[key];
        } else {
          this.$set(order, key, fresh[key]);
        }
      });
    },
    removeFromList(index) {
      this.orders.splice(index, 1);
    },
    getOrdersByPassengerId(id) {
      if(!id) return;
      this.loading = true;
      axios.get('booking', {
        params: {
          passenger_id: id,
          perPage: 100,
          status: 'ALL',
          from: companyNow().subtract(3, 'hours').format('YYYY-MM-DD HH:mm:ss'),
          to: companyNow().format('YYYY-MM-DD HH:mm:ss')
        }
      })
      .then((res) => {
        this.orders = res.data.data.data;
      })
      .catch((err) => {
        this.errorMessage = err.response.data.message;
      })
      .finally(() => {
        this.loading = false;
      })
    },
    loadData(page, statuses) {
      if (this.page < this.lastPage) {
        this.$store
          .dispatch("getOrdersPageData", {
            page: page,
            statuses: statuses,
            time: this.time,
            filter: "BOTH",
          })
          .then((response) => {
            console.log(response);
            this.page++;
            response.data.forEach((el) => {
              this.orders.push(el);
              this.$emit("loaded", el);
            });
          })
          .catch((error) => {
            this.errorMessage = error;
            // console.log(error);
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    scroll(event) {
      if(!this.passengerId) {
        console.log("-------EVENT------");
        console.log(Math.round(event.target.scrollTop), event.target.offsetHeight, event.target.scrollHeight);
        console.log("-------------");
        console.log("first one ", event.target.scrollTop + event.target.offsetHeight >= event.target.scrollHeight);
        console.log("second one ", this.page < this.lastPage);
        console.log("third one ", !this.loading);
        console.log("-------END-----");
        let diff = +(Math.abs((Math.round(event.target.scrollTop) + event.target.offsetHeight) - event.target.scrollHeight) <= 1);
        if (
          Math.round(event.target.scrollTop) + diff + event.target.offsetHeight >=
          event.target.scrollHeight &&
          this.page < this.lastPage &&
          !this.loading
          ) {
          console.log("Reached the bottom");
          this.loading = true;
          if (!this.multiStatus) this.loadData(this.page + 1, [this.status]);
          else {
            this.loadData(this.page + 1, this.statuses);
          
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tab-container {
  width: 100%;
  height: 100%;
  float: inline-start;
  overflow-y: overlay;
  max-height: 553px;
  padding: 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tab-container::-webkit-scrollbar {
  width: 0.2em;
}

.tab-container::-webkit-scrollbar-track {
  background-color: transparent;
}

.tab-container::-webkit-scrollbar-thumb {
  background-color: #c1c5d8;
  border-radius: 9999px;
}

/* Spinner */

.spring-spinner,
.spring-spinner * {
  box-sizing: border-box;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                    supported by Chrome, Edge, Opera and Firefox */
}

.spring-spinner {
  height: 100%;
  max-height: 24px;
  width: auto;
  max-width: 24px;
  margin: auto;
}

.spring-spinner .spring-spinner-part {
  overflow: hidden;
  max-height: 12px;
  height: 100%;
  width: auto;
  max-width: 24px;
}

.spring-spinner .spring-spinner-part.bottom {
  transform: rotate(180deg) scale(-1, 1);
}

.spring-spinner .spring-spinner-rotator {
  width: 24px;
  height: 24px;
  border: 2.5px solid transparent;
  border-right-color: white;
  border-top-color: #d9d9d9;
  border-radius: 50%;
  box-sizing: border-box;
  animation: spring-spinner-animation 3s ease-in-out infinite;
  transform: rotate(-200deg);
}

@keyframes spring-spinner-animation {
  50% {
    transform: rotate(115deg);
  }
}

// w-full text-center mb-16 text-gray
.message {
  width: 100%;
  text-align: center;
  float: inline-start;
  color: #788e98;
  margin-top: 12px;
}
</style>
